"use client";

import { c as _c } from "react/compiler-runtime";
import type { OnDragEndResponder } from "@hello-pangea/dnd";
import type { StackProps } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { selectIsEditable } from "#/features/project/model/projectSlice";
import { DraggableSelectBarList } from "#/features/selectBar/ui/DraggableSelectBarList";
import { DraggableSelectBarChip } from "#/features/selectBar/ui/SelectBarChip";
import type { UseTRPCQueryResult } from "#/server/api/trpc";
import type { PlaygroundTestCase } from "#/server/db/generated/browser";
import { api } from "#/shared/api";
import type { RouterOutputs } from "#/shared/api";
import { usePlaygroundSlugs } from "#/shared/hooks";
import { useI18nContext } from "#/shared/hooks";
import { useAppDispatch, useAppSelector } from "#/store/hooks";
import { CaseModal } from "./CaseModal";
type TestCaseBrief = Pick<PlaygroundTestCase, "id" | "order" | "slug" | "title">;
type TestCaseSelectBarProps = StackProps & {
  selectedProject: UseTRPCQueryResult<RouterOutputs["project"]["getBySlug"]>;
};
export const TestCaseSelectBar: React.FC<TestCaseSelectBarProps> = t0 => {
  const $ = _c(61);
  let restProps;
  let selectedProject;
  if ($[0] !== t0) {
    ({
      selectedProject,
      ...restProps
    } = t0);
    $[0] = t0;
    $[1] = restProps;
    $[2] = selectedProject;
  } else {
    restProps = $[1];
    selectedProject = $[2];
  }
  const {
    LL
  } = useI18nContext();
  const dispatch = useAppDispatch();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    projectSlug: t1,
    caseSlug: t2,
    setCase
  } = usePlaygroundSlugs();
  const projectSlug = t1 === undefined ? "" : t1;
  const caseSlug = t2 === undefined ? "" : t2;
  const invalidateQueries = async slug => {
    await trpcUtils.project.getBySlug.invalidate(projectSlug);
    await trpcUtils.project.getCaseBySlug.invalidate({
      projectId: selectedProject.data?.id || "",
      slug: slug || caseSlug
    });
  };
  const trpcUtils = api.useUtils();
  let t3;
  if ($[3] !== projectSlug || $[4] !== trpcUtils.project.getBySlug) {
    t3 = cases => {
      trpcUtils.project.getBySlug.setData(projectSlug, prevData => {
        if (!prevData) {
          return prevData;
        }
        return {
          ...prevData,
          cases
        };
      });
    };
    $[3] = projectSlug;
    $[4] = trpcUtils.project.getBySlug;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const updateCasesCache = t3;
  let t4;
  if ($[6] !== enqueueSnackbar || $[7] !== invalidateQueries || $[8] !== setCase) {
    t4 = {
      onSuccess: async data => {
        await invalidateQueries(data.slug);
        setCase(data.slug);
        enqueueSnackbar(`🧪 Test case "${data.title}" created successfully! 🎉`, {
          variant: "success"
        });
      }
    };
    $[6] = enqueueSnackbar;
    $[7] = invalidateQueries;
    $[8] = setCase;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const addCase = api.project.addCase.useMutation(t4);
  let t5;
  if ($[10] !== updateCasesCache) {
    t5 = {
      onSuccess: async data_0 => {
        updateCasesCache(data_0);
      }
    };
    $[10] = updateCasesCache;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const reorderCases = api.project.reorderCases.useMutation(t5);
  let t6;
  if ($[12] !== caseSlug || $[13] !== invalidateQueries || $[14] !== setCase) {
    t6 = {
      onSuccess: async data_1 => {
        await invalidateQueries(data_1.slug);
        if (caseSlug === data_1.slug) {
          setCase("");
        }
      }
    };
    $[12] = caseSlug;
    $[13] = invalidateQueries;
    $[14] = setCase;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const deleteCase = api.project.deleteCase.useMutation(t6);
  const cases_0 = selectedProject.data?.cases;
  const isLoading = selectedProject.isLoading || addCase.isPending || deleteCase.isPending;
  const isEditable = useAppSelector(selectIsEditable);
  let t7;
  if ($[16] !== caseSlug || $[17] !== selectedProject.data || $[18] !== setCase) {
    t7 = () => {
      if (caseSlug || !selectedProject.data) {
        return;
      }
      const firstCaseSlug = selectedProject.data.cases[0]?.slug;
      if (firstCaseSlug) {
        setCase(firstCaseSlug);
      }
    };
    $[16] = caseSlug;
    $[17] = selectedProject.data;
    $[18] = setCase;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== caseSlug || $[21] !== dispatch || $[22] !== selectedProject.data || $[23] !== setCase) {
    t8 = [caseSlug, selectedProject.data, dispatch, setCase];
    $[20] = caseSlug;
    $[21] = dispatch;
    $[22] = selectedProject.data;
    $[23] = setCase;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  useEffect(t7, t8);
  let t9;
  if ($[25] !== setCase) {
    t9 = testCase => {
      setCase(testCase.slug);
    };
    $[25] = setCase;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const handleCaseClick = t9;
  let t10;
  if ($[27] !== handleCaseClick) {
    t10 = testCase_0 => {
      handleCaseClick(testCase_0);
      setIsModalOpen(true);
    };
    $[27] = handleCaseClick;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const handleCaseEdit = t10;
  let t11;
  if ($[29] !== addCase || $[30] !== caseSlug || $[31] !== cases_0?.length || $[32] !== selectedProject.data) {
    t11 = () => {
      if (!selectedProject.data) {
        return;
      }
      const order = cases_0?.length ?? 0;
      addCase.mutate({
        projectId: selectedProject.data.id,
        order,
        referenceCaseSlug: caseSlug
      });
    };
    $[29] = addCase;
    $[30] = caseSlug;
    $[31] = cases_0?.length;
    $[32] = selectedProject.data;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const handleAddCase = t11;
  let t12;
  if ($[34] !== cases_0 || $[35] !== reorderCases || $[36] !== selectedProject.data || $[37] !== updateCasesCache) {
    t12 = result => {
      if (!result.destination || result.destination.index === result.source.index || !cases_0 || !selectedProject.data) {
        return;
      }
      const {
        source,
        destination
      } = result;
      const newList = Array.from(cases_0);
      const removed = newList.splice(source.index, 1);
      newList.splice(destination.index, 0, ...removed);
      updateCasesCache(newList);
      reorderCases.mutate({
        projectId: selectedProject.data.id,
        caseIds: newList.map(_temp)
      });
    };
    $[34] = cases_0;
    $[35] = reorderCases;
    $[36] = selectedProject.data;
    $[37] = updateCasesCache;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const onItemDragEnd = t12;
  let t13;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setIsModalOpen(false);
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== isModalOpen) {
    t14 = <CaseModal open={isModalOpen} onClose={t13} />;
    $[40] = isModalOpen;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const t15 = !selectedProject.data;
  let t16;
  if ($[42] !== LL || $[43] !== caseSlug || $[44] !== cases_0 || $[45] !== handleCaseClick || $[46] !== handleCaseEdit || $[47] !== isEditable || $[48] !== isLoading) {
    t16 = (_, droppableSnapshot) => cases_0?.map((testCase_1, index) => <DraggableSelectBarChip id={testCase_1.id} key={testCase_1.id} index={index} droppableSnapshot={droppableSnapshot} editLabel={LL.EDIT_TEST_CASE()} isCurrent={testCase_1.slug === caseSlug} isEditable={isEditable} label={testCase_1.title} disabled={isLoading} onClick={() => handleCaseClick(testCase_1)} onEditClick={() => handleCaseEdit(testCase_1)} />);
    $[42] = LL;
    $[43] = caseSlug;
    $[44] = cases_0;
    $[45] = handleCaseClick;
    $[46] = handleCaseEdit;
    $[47] = isEditable;
    $[48] = isLoading;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== handleAddCase || $[51] !== isEditable || $[52] !== isLoading || $[53] !== onItemDragEnd || $[54] !== restProps || $[55] !== t15 || $[56] !== t16) {
    t17 = <DraggableSelectBarList droppableId="droppable-solutions" isLoading={isLoading} isEmpty={t15} onItemDragEnd={onItemDragEnd} addItemTitle={"Add new test case \uD83E\uDDEA"} handleAddItem={handleAddCase} isEditable={isEditable} {...restProps}>{t16}</DraggableSelectBarList>;
    $[50] = handleAddCase;
    $[51] = isEditable;
    $[52] = isLoading;
    $[53] = onItemDragEnd;
    $[54] = restProps;
    $[55] = t15;
    $[56] = t16;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== t14 || $[59] !== t17) {
    t18 = <>{t14}{t17}</>;
    $[58] = t14;
    $[59] = t17;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  return t18;
};
function _temp(solution) {
  return solution.id;
}